import { useCallback, useMemo, useState } from "react";
import { useForm } from "@mantine/form";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Pagination,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowRight,
  IconSend,
} from "@tabler/icons-react";
import {
  useAccounts,
  useLabs,
  usePhysicians,
  useSurgeryCenters,
} from "../../hooks/useReferenceData";
import { reportDeliveryQueueService } from "../../services/workflows.service";
import type {
  BulkDeliveryCandidate,
  BulkDeliveryFilters,
  BulkDeliveryPlan,
  BulkDeliveryRecipient,
} from "../../services/workflows.service";

interface BulkDeliveryModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const PAGE_SIZE = 20;

function optionData(options: Array<{ value: string; label: string }>) {
  return options.map((option) => ({
    value: option.value,
    label: option.label,
  }));
}

function mergeCandidatePages(
  pages: BulkDeliveryPlan[]
): BulkDeliveryCandidate[] {
  const candidates = new Map<string, BulkDeliveryCandidate>();
  pages.forEach((page) =>
    page.items.forEach((item) => candidates.set(item.id, item))
  );
  return Array.from(candidates.values());
}

function mergeRecipients(
  candidates: BulkDeliveryCandidate[]
): BulkDeliveryRecipient[] {
  const recipients = new Map<string, BulkDeliveryRecipient>();
  candidates.forEach((candidate) => {
    candidate.recipients.forEach((recipient) => {
      const existing = recipients.get(recipient.key);
      if (!existing) {
        recipients.set(recipient.key, {
          ...recipient,
          methods: [...recipient.methods],
        });
        return;
      }
      const known = new Set(existing.methods.map((method) => method.configId));
      existing.methods.push(
        ...recipient.methods.filter((method) => !known.has(method.configId))
      );
    });
  });
  return Array.from(recipients.values());
}

export function BulkDeliveryModal({
  opened,
  onClose,
  onSuccess,
}: BulkDeliveryModalProps) {
  const [step, setStep] = useState<"filters" | "cases" | "recipients">(
    "filters"
  );
  const [plan, setPlan] = useState<BulkDeliveryPlan | null>(null);
  const [cases, setCases] = useState<BulkDeliveryCandidate[]>([]);
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([]);
  const [selectedConfigIdsByEntity, setSelectedConfigIdsByEntity] = useState<
    Record<string, string[]>
  >({});
  const [confirmationKey, setConfirmationKey] = useState(() =>
    crypto.randomUUID()
  );
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: accounts = [] } = useAccounts();
  const { data: surgeryCenters = [] } = useSurgeryCenters();
  const { data: labs = [] } = useLabs();
  const { data: physicians = [] } = usePhysicians();
  const form = useForm<BulkDeliveryFilters>({
    initialValues: {
      practiceIds: [],
      surgeryCenterIds: [],
      sendingLabIds: [],
      physicianIds: [],
      caseNumber: "",
    },
    validateInputOnBlur: true,
  });

  const filters = useMemo(() => form.values, [form.values]);
  const selectedCases = useMemo(
    () => cases.filter((candidate) => selectedCaseIds.includes(candidate.id)),
    [cases, selectedCaseIds]
  );
  const recipients = useMemo(
    () => mergeRecipients(selectedCases),
    [selectedCases]
  );

  const resetModal = useCallback(() => {
    setStep("filters");
    setPlan(null);
    setCases([]);
    setSelectedCaseIds([]);
    setSelectedConfigIdsByEntity({});
    setConfirmationKey(crypto.randomUUID());
    setPage(1);
    setError(null);
    form.reset();
  }, [form]);

  const handleClose = useCallback(() => {
    resetModal();
    onClose();
  }, [onClose, resetModal]);

  const applyPlan = useCallback(
    async (
      nextPage: number,
      nextSelectedCaseIds?: string[],
      nextPlanId?: string
    ) => {
      setLoading(true);
      setError(null);
      try {
        const result = await reportDeliveryQueueService.planBulkDelivery({
          filters,
          page: nextPage,
          pageSize: PAGE_SIZE,
          planId: nextPlanId,
          selectedCaseIds: nextSelectedCaseIds,
        });
        setPlan(result);
        setPage(result.page);
        setSelectedCaseIds(result.selectedCaseIds);
        setCases((current) => {
          const byId = new Map(current.map((item) => [item.id, item]));
          result.items.forEach((item) => byId.set(item.id, item));
          return Array.from(byId.values());
        });
        setSelectedConfigIdsByEntity((current) => {
          const next = { ...current };
          result.recipients.forEach((recipient) => {
            if (next[recipient.key] !== undefined) return;
            next[recipient.key] = recipient.methods.map(
              (method) => method.configId
            );
          });
          return next;
        });
      } catch {
        setError(
          "Unable to load eligible reports. Please adjust the filters and try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const handleApplyFilters = useCallback(
    async (values: BulkDeliveryFilters) => {
      form.setValues(values);
      await applyPlan(1);
      setStep("cases");
    },
    [applyPlan, form]
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      void applyPlan(nextPage, selectedCaseIds, plan?.planId);
    },
    [applyPlan, plan?.planId, selectedCaseIds]
  );

  const handleCaseSelectionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const caseId = event.currentTarget.dataset.caseId;
      if (!caseId) return;
      const checked = event.currentTarget.checked;
      setSelectedCaseIds((current) =>
        checked
          ? Array.from(new Set([...current, caseId]))
          : current.filter((id) => id !== caseId)
      );
    },
    []
  );

  const handleSelectAllCases = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const pageIds = (plan?.items || []).map((item) => item.id);
      const checked = event.currentTarget.checked;
      setSelectedCaseIds((current) => {
        const next = new Set(current);
        pageIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
        return Array.from(next);
      });
    },
    [plan?.items]
  );

  const handleReviewRecipients = useCallback(async () => {
    if (!plan) return;
    setLoading(true);
    setError(null);
    try {
      const pages = [plan];
      const remainingPages = await Promise.all(
        Array.from({ length: Math.max(plan.totalPages - 1, 0) }, (_, index) =>
          reportDeliveryQueueService.planBulkDelivery({
            filters,
            page: index + 2,
            pageSize: PAGE_SIZE,
            planId: plan.planId,
            selectedCaseIds,
          })
        )
      );
      pages.push(...remainingPages);
      const allCases = mergeCandidatePages(pages);
      setCases(allCases);
      setSelectedConfigIdsByEntity((current) => {
        const next = { ...current };
        const merged = mergeRecipients(allCases);
        merged.forEach((recipient) => {
          if (next[recipient.key] !== undefined) return;
          next[recipient.key] = recipient.methods.map(
            (method) => method.configId
          );
        });
        return next;
      });
      setStep("recipients");
    } catch {
      setError("Unable to prepare the recipient review. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters, plan, selectedCaseIds]);

  const handleRecipientMethodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const configId = event.currentTarget.dataset.configId;
      if (!configId) return;
      const entityKey = event.currentTarget.dataset.entityKey;
      if (!entityKey) return;
      const checked = event.currentTarget.checked;
      setSelectedConfigIdsByEntity((current) => {
        const selected = new Set(current[entityKey] || []);
        if (checked) selected.add(configId);
        else selected.delete(configId);
        return { ...current, [entityKey]: Array.from(selected) };
      });
    },
    []
  );

  const isRecipientMethodSelected = useCallback(
    (recipient: BulkDeliveryRecipient, configId: string): boolean => {
      return (selectedConfigIdsByEntity[recipient.key] || []).includes(
        configId
      );
    },
    [selectedConfigIdsByEntity]
  );

  const handleConfirm = useCallback(async () => {
    if (!plan || selectedCaseIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const result = await reportDeliveryQueueService.confirmBulkDelivery(
        plan.planId,
        confirmationKey,
        selectedConfigIdsByEntity
      );
      notifications.show({
        title: "Bulk delivery scheduled",
        message: `${result.queuedCount} delivery item${
          result.queuedCount === 1 ? "" : "s"
        } queued with the normal delay.`,
        color: "green",
      });
      onSuccess?.();
      handleClose();
    } catch {
      setError(
        "The bulk delivery could not be confirmed. Refresh the plan and try again."
      );
    } finally {
      setLoading(false);
    }
  }, [
    confirmationKey,
    handleClose,
    onSuccess,
    plan,
    selectedCaseIds,
    selectedConfigIdsByEntity,
  ]);

  const handleBackToCases = useCallback(() => setStep("cases"), []);
  const selectedPageCount = useMemo(
    () =>
      (plan?.items || []).filter((item) => selectedCaseIds.includes(item.id))
        .length,
    [plan?.items, selectedCaseIds]
  );

  return (
    <Modal.Root opened={opened} onClose={handleClose} size="xl">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>New Bulk Delivery</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Stack gap="md">
            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                title="Bulk delivery unavailable"
                role="alert"
              >
                {error}
              </Alert>
            )}
            {step === "filters" && (
              <form onSubmit={form.onSubmit(handleApplyFilters)}>
                <Stack gap="md">
                  <Text size="sm" c="dimmed">
                    Find signed-off reports to schedule together.
                  </Text>
                  <MultiSelect
                    label="Practice"
                    data={optionData(accounts)}
                    searchable
                    clearable
                    {...form.getInputProps("practiceIds")}
                  />
                  <MultiSelect
                    label="Surgical center"
                    data={optionData(surgeryCenters)}
                    searchable
                    clearable
                    {...form.getInputProps("surgeryCenterIds")}
                  />
                  <MultiSelect
                    label="Sending lab"
                    data={optionData(labs)}
                    searchable
                    clearable
                    {...form.getInputProps("sendingLabIds")}
                  />
                  <MultiSelect
                    label="Physician"
                    data={optionData(physicians)}
                    searchable
                    clearable
                    {...form.getInputProps("physicianIds")}
                  />
                  <TextInput
                    label="Case number"
                    placeholder="Any partial case number"
                    {...form.getInputProps("caseNumber")}
                  />
                  <Button
                    type="submit"
                    loading={loading}
                    leftSection={<IconArrowRight size={16} />}
                  >
                    Find reports
                  </Button>
                </Stack>
              </form>
            )}
            {step === "cases" && (
              <Stack gap="md">
                <Group justify="space-between">
                  <Text size="sm">
                    {plan?.total || 0} eligible report
                    {plan?.total === 1 ? "" : "s"} found
                  </Text>
                  {loading && <Loader size="sm" />}
                </Group>
                <Checkbox
                  label="Select every report on this page"
                  checked={
                    selectedPageCount === (plan?.items.length || 0) &&
                    selectedPageCount > 0
                  }
                  onChange={handleSelectAllCases}
                />
                <Stack gap="xs">
                  {(plan?.items || []).map((candidate) => (
                    <Paper key={candidate.id} withBorder p="sm">
                      <Checkbox
                        data-case-id={candidate.id}
                        label={`${candidate.caseNumber} · report version ${candidate.reportVersion}`}
                        checked={selectedCaseIds.includes(candidate.id)}
                        onChange={handleCaseSelectionChange}
                      />
                    </Paper>
                  ))}
                </Stack>
                {(plan?.totalPages || 0) > 1 && (
                  <Pagination
                    value={page}
                    total={plan?.totalPages || 1}
                    onChange={handlePageChange}
                  />
                )}
                <Group justify="space-between">
                  <Button
                    variant="default"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={resetModal}
                  >
                    Change filters
                  </Button>
                  <Button
                    disabled={selectedCaseIds.length === 0}
                    loading={loading}
                    rightSection={<IconArrowRight size={16} />}
                    onClick={handleReviewRecipients}
                  >
                    Review recipients
                  </Button>
                </Group>
              </Stack>
            )}
            {step === "recipients" && (
              <Stack gap="md">
                <Text size="sm">
                  Choose zero or more configured methods for each recipient.
                  Leaving all methods unchecked skips that recipient.
                </Text>
                {recipients.map((recipient) => (
                  <Paper key={recipient.key} withBorder p="md">
                    <Stack gap="xs">
                      <Text fw={600}>{recipient.name}</Text>
                      <Text size="xs" c="dimmed">
                        {recipient.type.replace("_", " ")}
                      </Text>
                      {recipient.methods.map((method) => (
                        <Checkbox
                          key={method.configId}
                          data-entity-key={recipient.key}
                          data-config-id={method.configId}
                          label={`${method.deliveryMethodName}${
                            method.destination ? ` · ${method.destination}` : ""
                          }`}
                          checked={isRecipientMethodSelected(
                            recipient,
                            method.configId
                          )}
                          onChange={handleRecipientMethodChange}
                        />
                      ))}
                    </Stack>
                  </Paper>
                ))}
                {recipients.length === 0 && (
                  <Text c="dimmed">
                    No configured recipients were found. Confirmation will not
                    create deliveries.
                  </Text>
                )}
                <Group justify="space-between">
                  <Button
                    variant="default"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={handleBackToCases}
                  >
                    Back to reports
                  </Button>
                  <Button
                    loading={loading}
                    disabled={selectedCaseIds.length === 0}
                    leftSection={<IconSend size={16} />}
                    onClick={handleConfirm}
                  >
                    Confirm bulk delivery
                  </Button>
                </Group>
              </Stack>
            )}
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
